import { CaptureContext } from "@zumer/snapdom";

/**
 * Snapdom plugin that fixes scrolled react-virtuoso lists (legends) in captures.
 *
 * Problem:
 *   Virtuoso renders a scroller ([data-virtuoso-scroller]) containing an
 *   absolutely-positioned viewport ([data-viewport-type], inline `top: 0`)
 *   whose item list positions the rendered rows with `padding-top ≈ scrollTop`.
 *   When snapdom clones a scrolled container it compensates by (a) bumping
 *   absolutely-positioned descendants down by +scrollTop and (b) wrapping the
 *   scroller's children in a `translate(0, -scrollTop)` div. For Virtuoso the
 *   two cancel out on the viewport, leaving the rows at padding-top below the
 *   clipped scroller — so a scrolled legend renders blank in the screenshot.
 *
 *   Additionally, snapdom's translate wrapper is `display: inline-block`; with
 *   only the absolutely-positioned viewport inside it has zero height and sits
 *   on the scroller's text baseline, shifting the whole list down by the font
 *   ascent (~half a row) and clipping the bottom of the last visible item.
 *
 * Solution:
 *   After the clone is created, make the wrapper block-level and reset the
 *   cloned viewport's `top` back to 0 so snapdom's own -scrollTop translate
 *   takes effect and the capture shows the same rows, at the same scroll
 *   offset, that the user sees on screen.
 */

const SCROLLER_SELECTOR = "[data-virtuoso-scroller]";
const VIEWPORT_SELECTOR = "[data-viewport-type]";

/** Reset snapdom's +scrollTop bump on each scrolled Virtuoso viewport clone. */
const fixClonedVirtuosoScrollers = (liveRoot: Element, clone: HTMLElement): void => {
  const liveScrollers = Array.from(liveRoot.querySelectorAll(SCROLLER_SELECTOR));
  const clonedScrollers = Array.from(clone.querySelectorAll(SCROLLER_SELECTOR));

  liveScrollers.forEach((liveScroller, index) => {
    const clonedScroller = clonedScrollers[index];
    if (!(clonedScroller instanceof HTMLElement)) return;

    if (liveScroller.scrollTop === 0) {
      // Unscrolled: snapdom's overflow:auto clip lands a few pixels tighter than
      // the on-screen viewport, slicing the last visible row mid-line. Drop the
      // inner clip so the row renders in full. The legend only has its top rows
      // (plus overscan) mounted at scrollTop 0, and the screenshot frame
      // (overflow:hidden) still bounds the legend, so overscan rows below stay
      // clipped at the box edge exactly as they are on screen.
      clonedScroller.style.overflow = "visible";
      return;
    }

    // Scrolled: keep the scroller's overflow clip (overflow:visible would let
    // scrolled-past rows bleed up over the legend title). snapdom's translate
    // wrapper is inline-block; with zero height it sits on the text baseline,
    // shifting the list down by the font ascent and clipping the last item.
    const wrapper = clonedScroller.firstElementChild;
    if (wrapper instanceof HTMLElement) wrapper.style.display = "block";

    const viewport = clonedScroller.querySelector<HTMLElement>(VIEWPORT_SELECTOR);
    if (viewport) viewport.style.top = "0px";
  });
};

const virtuosoScrollPlugin = {
  name: "virtuoso-scroll",
  async afterClone(context: CaptureContext) {
    if (context.element && context.clone instanceof HTMLElement) {
      fixClonedVirtuosoScrollers(context.element, context.clone);
    }
  }
};

export default virtuosoScrollPlugin;
